/**
 * ONNX-based 2D→3D pose lifters.
 *
 * `pickPoseLifter()` returns the right adapter for the current device
 * tier. PC machines get MotionBERT (243-frame window, higher accuracy);
 * mobile devices get VideoPose3D (81-frame window, lighter compute).
 *
 * docs/migration-plan.md Phase 12.
 */

import type { PoseLifterAdapter } from '../../core/PoseLifter';
import { MotionBertLifter, type MotionBertOptions } from './MotionBert';
import { VideoPose3DLifter, type VideoPose3DOptions } from './VideoPose3D';

export { MotionBertLifter, VideoPose3DLifter };
export type { MotionBertOptions, VideoPose3DOptions };

export type DeviceTier = 'pc' | 'mobile';

export interface PickPoseLifterOpts {
  tier?: DeviceTier;
  motionBert?: MotionBertOptions;
  videoPose3D?: VideoPose3DOptions;
}

export function pickPoseLifter(opts: PickPoseLifterOpts = {}): PoseLifterAdapter {
  const tier = opts.tier ?? detectTier();
  if (tier === 'mobile') {
    return new VideoPose3DLifter(opts.videoPose3D);
  }
  return new MotionBertLifter(opts.motionBert);
}

function detectTier(): DeviceTier {
  if (typeof navigator === 'undefined') return 'pc';
  const ua = navigator.userAgent;
  // Same heuristic as the existing mobile-redirect script in index.html.
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  return isMobile ? 'mobile' : 'pc';
}
